import React, { useMemo, useState } from "react";
import {
  MdAdd,
  MdRemove,
  MdDeleteOutline,
  MdDragIndicator,
  MdOutlineEco,
} from "react-icons/md";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ComponentAmount,
  RecipeComponentFormValue,
  RecipeSectionFormValue,
} from "../../types/recipe";
import {
  clampPortion,
  computeComponentContribution,
  computeLivePreviewMacros,
  dividePerServing,
  PORTION_MIN_G,
  PORTION_MAX_G,
  SECTION_NAME_MAX,
} from "../../utils/recipeMacros";
import {
  GRAM_UNIT,
  QUANTITY_MAX,
  QUANTITY_MIN,
  amountAfterUnitChange,
  clampQuantity,
  ensureUnits,
  findUnit,
  formatAmountWithGrams,
  formatUnitLabel,
  isGramUnit,
  resolveAmount,
} from "../../utils/measurementUnits";
import {
  VISIBLE_NUTRIENTS,
  NutrientTile,
  formatMacroValue,
  formatNutrientAmount,
} from "./nutrientMeta";

interface RecipeComponentsSectionProps {
  components: RecipeComponentFormValue[];
  sections: RecipeSectionFormValue[];
  servings: number;
  disabled?: boolean;
  onAddComponentToSection: (sectionId: string) => void;
  onUpdateAmount: (key: string, amount: ComponentAmount) => void;
  onRemoveComponent: (key: string) => void;
  onAddSection: () => void;
  onRenameSection: (id: string, name: string) => void;
  onRemoveSection: (id: string) => void;
  onReorderComponents: (next: RecipeComponentFormValue[]) => void;
  onReorderSections: (next: RecipeSectionFormValue[]) => void;
  errors?: {
    components?: string;
    sections?: Record<string, string>;
    portions?: Record<string, string>;
  };
}

const BODY_PREFIX = "body:";

type NutritionView = "per_serving" | "total";

const sectionIdFromOver = (
  overId: string,
  components: RecipeComponentFormValue[]
): string | null => {
  if (overId.startsWith(BODY_PREFIX)) return overId.slice(BODY_PREFIX.length);
  if (overId.startsWith("sec-")) return overId;
  const comp = components.find((c) => c.key === overId);
  return comp ? comp.sectionId : null;
};

// Sortable component row

interface SortableComponentRowProps {
  component: RecipeComponentFormValue;
  error?: string;
  onUpdateAmount: (key: string, amount: ComponentAmount) => void;
  onRemoveComponent: (key: string) => void;
}

const ROW_CONTROL_CLASS =
  "appearance-none border-0 bg-[transparent] py-1 text-sm text-navy-700 outline-none focus:ring-0 dark:text-white dark:[color-scheme:dark]";

const SortableComponentRow: React.FC<SortableComponentRowProps> = ({
  component,
  error,
  onUpdateAmount,
  onRemoveComponent,
}) => {
  const units = ensureUnits(component.food.measurement_units);
  // A unit the ingredient no longer offers reads and saves as grams.
  const unit = findUnit(units, component.measurement_unit_label) ?? GRAM_UNIT;
  const isGrams = isGramUnit(unit);
  const shownValue = isGrams
    ? component.portion_grams
    : component.quantity ?? 0;

  const amountFor = (value: number, target: typeof unit): ComponentAmount =>
    isGramUnit(target)
      ? {
          portion_grams: clampPortion(value),
          quantity: null,
          measurement_unit_label: null,
        }
      : resolveAmount(clampQuantity(value), target);

  const handleValueChange = (raw: string) => {
    onUpdateAmount(component.key, amountFor(parseFloat(raw), unit));
  };

  const handleUnitChange = (label: string) => {
    const next = findUnit(units, label) ?? GRAM_UNIT;
    const text = amountAfterUnitChange(String(shownValue), unit, next);
    onUpdateAmount(component.key, amountFor(Number(text), next));
  };

  const contribution = computeComponentContribution(component);
  const caption = [
    isGrams
      ? null
      : `= ${formatAmountWithGrams(component.portion_grams, null, null)}`,
    ...(contribution
      ? [
          `${formatNutrientAmount("calories", contribution.calories)} kcal`,
          `P ${formatNutrientAmount("protein", contribution.protein)} g`,
          `C ${formatNutrientAmount("carbs", contribution.carbs)} g`,
          `F ${formatNutrientAmount("fat", contribution.fat)} g`,
        ]
      : []),
  ]
    .filter(Boolean)
    .map((part) => part.replace(/ /g, "\u00a0"))
    .join(" · ");

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: component.key,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div
        className={`flex flex-wrap items-center gap-2 rounded-md bg-gray-50 px-2 py-2 dark:bg-gray-800 ${
          error ? "ring-1 ring-red-400" : ""
        }`}
      >
        <button
          type="button"
          className="cursor-grab touch-none text-gray-400 hover:text-gray-600 active:cursor-grabbing dark:hover:text-gray-200"
          {...attributes}
          {...listeners}
          aria-label="Drag ingredient"
        >
          <MdDragIndicator className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <span className="block break-words font-medium text-navy-700 dark:text-white sm:truncate">
            {component.food.name}
          </span>
          {caption && (
            <span className="block break-words text-sm text-gray-700 dark:text-gray-300 sm:truncate">
              {caption}
            </span>
          )}
        </div>
        <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
          <div className="flex min-w-0 flex-1 items-center rounded-md border border-gray-300 bg-white dark:border-gray-600 dark:bg-navy-700 sm:flex-none">
            <input
              type="number"
              min={isGrams ? PORTION_MIN_G : QUANTITY_MIN}
              max={isGrams ? PORTION_MAX_G : QUANTITY_MAX}
              step="any"
              aria-label={`Quantity of ${component.food.name}`}
              value={shownValue}
              onChange={(e) => handleValueChange(e.target.value)}
              className={`min-w-0 flex-1 px-2 text-right sm:w-28 sm:flex-none [&::-webkit-inner-spin-button]:ml-2 ${ROW_CONTROL_CLASS}`}
            />
            <select
              aria-label={`Unit of ${component.food.name}`}
              value={unit.label}
              onChange={(e) => handleUnitChange(e.target.value)}
              className={`w-28 shrink-0 appearance-none truncate rounded-none border-l border-gray-300 bg-select-chevron bg-[length:1rem_1rem] bg-[position:right_0.25rem_center] bg-no-repeat pl-3 pr-6 dark:border-gray-600 sm:w-32 ${ROW_CONTROL_CLASS}`}
            >
              {units.map((u) => (
                <option key={u.label} value={u.label}>
                  {formatUnitLabel(u.label)}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={() => onRemoveComponent(component.key)}
            className="shrink-0 p-1 text-red-600 hover:text-red-800"
            aria-label="Remove ingredient"
          >
            <MdRemove className="h-4 w-4" />
          </button>
        </div>
      </div>
      {error && <p className="mt-1 pl-7 text-sm text-red-600">{error}</p>}
    </div>
  );
};

// --- Sortable section ---

interface SortableSectionProps {
  section: RecipeSectionFormValue;
  sectionComponents: RecipeComponentFormValue[];
  disabled: boolean;
  sectionError?: string;
  portionErrors?: Record<string, string>;
  isOnlySection: boolean;
  onAddComponentToSection: (sectionId: string) => void;
  onUpdateAmount: (key: string, amount: ComponentAmount) => void;
  onRemoveComponent: (key: string) => void;
  onRenameSection: (id: string, name: string) => void;
  onRemoveSection: (id: string) => void;
}

const SortableSection: React.FC<SortableSectionProps> = ({
  section,
  sectionComponents,
  disabled,
  sectionError,
  portionErrors,
  isOnlySection,
  onAddComponentToSection,
  onUpdateAmount,
  onRemoveComponent,
  onRenameSection,
  onRemoveSection,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: section.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const { setNodeRef: setBodyRef, isOver } = useDroppable({
    id: `${BODY_PREFIX}${section.id}`,
  });

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rounded-lg border border-gray-200 p-3 dark:border-gray-800 dark:bg-navy-700 sm:p-4"
    >
      <div className="mb-3 flex items-center gap-2">
        <button
          type="button"
          className="cursor-grab touch-none text-gray-400 hover:text-gray-600 active:cursor-grabbing dark:hover:text-gray-200"
          {...attributes}
          {...listeners}
          aria-label={`Drag section ${section.name}`}
        >
          <MdDragIndicator className="h-5 w-5" />
        </button>
        <input
          type="text"
          value={section.name}
          maxLength={SECTION_NAME_MAX}
          onChange={(e) => onRenameSection(section.id, e.target.value)}
          aria-label="Section name"
          className={`min-w-0 flex-1 rounded-md border bg-[transparent] px-2 py-1 text-sm font-bold uppercase text-biafit-primary outline-none placeholder:font-normal placeholder:normal-case placeholder:text-gray-400 focus:border-biafit-primary focus:ring-1 focus:ring-biafit-primary ${
            sectionError
              ? "border-red-500 dark:border-red-400"
              : "border-transparent hover:border-gray-300 dark:hover:border-gray-600"
          }`}
          placeholder="Enter section name"
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() => onAddComponentToSection(section.id)}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded border border-biafit-primary bg-biafit-primary text-white hover:bg-biafit-secondary disabled:opacity-50"
          aria-label={`Add ingredient to ${section.name}`}
          title="Add ingredient"
        >
          <MdAdd className="h-4 w-4" />
        </button>
        <button
          type="button"
          disabled={isOnlySection}
          onClick={() => onRemoveSection(section.id)}
          title={isOnlySection ? "Keep at least one section" : "Delete section"}
          className="shrink-0 text-red-600 hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label={`Delete section ${section.name}`}
        >
          <MdDeleteOutline className="h-5 w-5" />
        </button>
      </div>

      {sectionError && (
        <p className="mb-2 pl-7 text-sm text-red-600">{sectionError}</p>
      )}

      <div
        ref={setBodyRef}
        className={`space-y-2 rounded-md ${
          isOver
            ? "bg-biafit-primary bg-opacity-5 ring-1 ring-biafit-primary ring-opacity-40"
            : ""
        }`}
      >
        <SortableContext
          items={sectionComponents.map((c) => c.key)}
          strategy={verticalListSortingStrategy}
        >
          {sectionComponents.length === 0 ? (
            <button
              type="button"
              disabled={disabled}
              onClick={() => onAddComponentToSection(section.id)}
              className="flex w-full flex-col items-center gap-1 rounded-md border border-dashed border-gray-300 py-4 text-sm text-gray-500 transition-colors hover:border-biafit-primary hover:bg-biafit-primary/5 disabled:opacity-50 dark:border-gray-600 dark:text-gray-400 dark:hover:border-biafit-primary"
            >
              <MdOutlineEco className="h-6 w-6 text-gray-400" />
              Add first ingredient
            </button>
          ) : (
            sectionComponents.map((component) => (
              <SortableComponentRow
                key={component.key}
                component={component}
                error={portionErrors?.[component.key]}
                onUpdateAmount={onUpdateAmount}
                onRemoveComponent={onRemoveComponent}
              />
            ))
          )}
        </SortableContext>
      </div>
    </div>
  );
};

const RecipeComponentsSection: React.FC<RecipeComponentsSectionProps> = ({
  components,
  sections,
  servings,
  disabled = false,
  onAddComponentToSection,
  onUpdateAmount,
  onRemoveComponent,
  onAddSection,
  onRenameSection,
  onRemoveSection,
  onReorderComponents,
  onReorderSections,
  errors,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [nutritionView, setNutritionView] =
    useState<NutritionView>("per_serving");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const displayMacros = useMemo(() => {
    const totals = computeLivePreviewMacros(components);
    return nutritionView === "total"
      ? totals
      : dividePerServing(totals, servings);
  }, [components, servings, nutritionView]);

  const hasComponents = components.length > 0;

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    const activeIdStr = String(active.id);
    const overIdStr = String(over.id);
    if (activeIdStr === overIdStr) return;

    // Section reorder
    if (activeIdStr.startsWith("sec-")) {
      const targetSectionId = sectionIdFromOver(overIdStr, components);
      const oldIndex = sections.findIndex((s) => s.id === activeIdStr);
      const newIndex = sections.findIndex((s) => s.id === targetSectionId);
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
      onReorderSections(arrayMove(sections, oldIndex, newIndex));
      return;
    }

    // Component move
    const targetSectionId = sectionIdFromOver(overIdStr, components);
    if (!targetSectionId) return;

    const activeComp = components.find((c) => c.key === activeIdStr);
    if (!activeComp) return;

    // Onto another component
    const overIsComponent =
      overIdStr.startsWith("cmp-") && overIdStr !== activeIdStr;
    if (overIsComponent) {
      const oldIndex = components.findIndex((c) => c.key === activeIdStr);
      const newIndex = components.findIndex((c) => c.key === overIdStr);
      if (oldIndex < 0 || newIndex < 0) return;
      let next = arrayMove(components, oldIndex, newIndex);
      if (activeComp.sectionId !== targetSectionId) {
        next = next.map((c) =>
          c.key === activeIdStr ? { ...c, sectionId: targetSectionId } : c
        );
      }
      onReorderComponents(next);
      return;
    }

    const without = components.filter((c) => c.key !== activeIdStr);
    const updated: RecipeComponentFormValue = {
      ...activeComp,
      sectionId: targetSectionId,
    };
    let lastIdxOfSection = -1;
    for (let i = 0; i < without.length; i += 1) {
      if (without[i].sectionId === targetSectionId) lastIdxOfSection = i;
    }
    const next =
      lastIdxOfSection === -1
        ? [...without, updated]
        : [
            ...without.slice(0, lastIdxOfSection + 1),
            updated,
            ...without.slice(lastIdxOfSection + 1),
          ];
    onReorderComponents(next);
  };

  const activeComponent = activeId
    ? components.find((c) => c.key === activeId)
    : null;
  const activeSection = activeId
    ? sections.find((s) => s.id === activeId)
    : null;

  const macroCell = (meta: (typeof VISIBLE_NUTRIENTS)[number]) => (
    <NutrientTile key={meta.key} meta={meta}>
      <div className="whitespace-nowrap text-base font-bold text-navy-700 dark:text-white sm:text-lg">
        {hasComponents
          ? `${formatMacroValue(displayMacros[meta.key], meta.decimals)} ${
              meta.unit
            }`
          : "—"}
      </div>
      <div className={`text-xs font-bold ${meta.accent}`}>{meta.label}</div>
    </NutrientTile>
  );

  const viewButton = (view: NutritionView, label: string) => (
    <button
      type="button"
      onClick={() => setNutritionView(view)}
      aria-pressed={nutritionView === view}
      className={`px-3 py-1 text-xs font-medium ${
        nutritionView === view
          ? "bg-biafit-primary text-white"
          : "bg-white text-gray-600 hover:bg-gray-50 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div>
      {/* Nutrition computed from the ingredients */}
      <div className="mb-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Nutrition
          </span>
          <div className="inline-flex overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
            {viewButton("per_serving", "Per serving")}
            {viewButton("total", "Total")}
          </div>
        </div>
        {!hasComponents && (
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
            Nutrition is computed from the ingredients once they are added.
          </p>
        )}
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {VISIBLE_NUTRIENTS.map(macroCell)}
        </div>
      </div>

      <span className="mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300">
        Ingredients
      </span>

      {!hasComponents && errors?.components && (
        <p className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-900/30 dark:text-red-400">
          {errors.components}
        </p>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveId(null)}
      >
        <SortableContext
          items={sections.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-4">
            {sections.map((section) => (
              <SortableSection
                key={section.id}
                section={section}
                sectionComponents={components.filter(
                  (c) => c.sectionId === section.id
                )}
                disabled={disabled}
                sectionError={errors?.sections?.[section.id]}
                portionErrors={errors?.portions}
                isOnlySection={sections.length <= 1}
                onAddComponentToSection={onAddComponentToSection}
                onUpdateAmount={onUpdateAmount}
                onRemoveComponent={onRemoveComponent}
                onRenameSection={onRenameSection}
                onRemoveSection={onRemoveSection}
              />
            ))}
          </div>
        </SortableContext>

        <DragOverlay>
          {activeComponent ? (
            <div className="flex items-center gap-2 rounded-md border border-biafit-primary bg-white px-3 py-2 shadow-lg dark:bg-navy-800">
              <MdDragIndicator className="h-5 w-5 text-gray-400" />
              <span className="font-medium text-navy-700 dark:text-white">
                {activeComponent.food.name}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {formatAmountWithGrams(
                  activeComponent.portion_grams,
                  activeComponent.quantity,
                  activeComponent.measurement_unit_label
                )}
              </span>
            </div>
          ) : activeSection ? (
            <div className="rounded-lg border border-biafit-primary bg-white px-4 py-3 shadow-lg dark:bg-navy-800">
              <span className="font-semibold text-navy-700 dark:text-white">
                {activeSection.name || "Section"}
              </span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <button
        type="button"
        disabled={disabled}
        onClick={onAddSection}
        className="mt-4 flex w-full items-center justify-center rounded-md border border-biafit-primary py-2 text-sm font-medium text-biafit-primary transition-colors hover:bg-biafit-primary/5 disabled:opacity-50"
      >
        <MdAdd className="mr-1 h-4 w-4" />
        Add New Section
      </button>
    </div>
  );
};

export default RecipeComponentsSection;
